import React, { useEffect, useMemo, useState } from 'react';

import PeopleIcon from '@mui/icons-material/PeopleAltOutlined';
import { makeStyles } from '@mui/styles';

import { EmployeeRole } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import {
  PodShuffleEmployeeForecast,
  PodShufflePodForecast,
} from '@vestahealthcare/common/models';
import { EMPTY } from '@vestahealthcare/common/utils/constants';

import {
  Card,
  Collapse,
  CollapseIcon,
  Colors,
  IconButton,
  PanelInfo,
  Spinner,
} from 'styleguide-v2';

import RecursiveTable, {
  RecursiveTableData,
} from 'dash/src/components/RecursiveTable';

import { showGlobalError } from '../../../../../components/GlobalMessage';
import {
  getPODShuffleEmployeeForecast,
  getPODShufflePODForecast,
} from '../../../../../services/PodServices';

const useStyles = makeStyles({
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '1rem',
    padding: '1rem',
  },
  card: {
    padding: '1.5rem',
  },
  podHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #e0e0e0',
    cursor: 'pointer',
  },
  podTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#333',
  },
  totalInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  totalText: {
    fontSize: '1.2rem',
    fontWeight: 600,
  },
  tableContainer: {
    '& table': {
      marginTop: 0,
    },
  },
});

interface Props {
  setLoading: (loading: boolean) => void;
}

interface PodForecastData {
  id: number;
  name: string;
  totalAssigned: number;
  totalCapacity: number;
  allocations?: RecursiveTableData[];
}

interface PodCardState {
  isOpen: boolean;
  isLoading: boolean;
  employees?: PodShuffleEmployeeForecast[];
}

export const PODShuffleForecastTab = ({ setLoading }: Props) => {
  const styles = useStyles();
  const [loading, setLocalLoading] = useState<boolean>(false);
  const [podForecasts, setPodForecasts] = useState<PodShufflePodForecast[]>([]);
  const [podStates, setPodStates] = useState<Record<number, PodCardState>>({});

  const fetchPodForecast = async () => {
    setLocalLoading(true);
    setLoading(true);
    try {
      const podShuffleForecast = await getPODShufflePODForecast();
      setPodForecasts(podShuffleForecast);
      const initialStates: Record<number, PodCardState> = {};
      podShuffleForecast.forEach((pod) => {
        initialStates[pod.podId] = {
          isOpen: false,
          isLoading: false,
        };
      });
      setPodStates(initialStates);
    } catch (error) {
      showGlobalError(error as string);
      setPodForecasts([]);
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  };

  const fetchEmployeesForPod = async (podId: number) => {
    setPodStates((prev) => ({
      ...prev,
      [podId]: { ...prev[podId], isLoading: true },
    }));

    try {
      const employees = await getPODShuffleEmployeeForecast(podId);
      setPodStates((prev) => ({
        ...prev,
        [podId]: {
          ...prev[podId],
          isLoading: false,
          employees,
        },
      }));
    } catch (error) {
      showGlobalError(error as string);
      setPodStates((prev) => ({
        ...prev,
        [podId]: {
          ...prev[podId],
          isLoading: false,
          employees: [],
        },
      }));
    }
  };

  const handleTogglePod = async (podId: number) => {
    const currentState = podStates[podId];

    if (!currentState.isOpen && !currentState.employees) {
      await fetchEmployeesForPod(podId);
      setPodStates((prev) => ({
        ...prev,
        [podId]: {
          ...prev[podId],
          isOpen: true,
        },
      }));
    } else {
      setPodStates((prev) => ({
        ...prev,
        [podId]: {
          ...prev[podId],
          isOpen: !prev[podId].isOpen,
        },
      }));
    }
  };

  useEffect(() => {
    fetchPodForecast();
  }, []);

  const transformedData = useMemo<PodForecastData[]>(() => {
    if (!podForecasts.length) return [];

    return podForecasts.map((pod) => {
      const podState = podStates[pod.podId];
      const podEmployees = podState?.employees || [];

      let allocations: RecursiveTableData[] | undefined;

      if (podEmployees.length > 0) {
        const roleGroups = podEmployees.reduce((acc, emp) => {
          if (!acc[emp.role]) {
            acc[emp.role] = {
              role: emp.role,
              employees: [],
              totalAssigned: 0,
              totalCapacity: 0,
            };
          }
          acc[emp.role].employees.push(emp);
          acc[emp.role].totalAssigned += emp.allocatedMembers;
          acc[emp.role].totalCapacity += emp.maxMembers;
          return acc;
        }, {} as Record<string, { role: string; employees: PodShuffleEmployeeForecast[]; totalAssigned: number; totalCapacity: number }>);

        allocations = Object.values(roleGroups).map((group) => ({
          name:
            EmployeeRole.byKey[group.role]?.toString() || group.role || EMPTY,
          column1: group.totalAssigned,
          column2: group.totalCapacity,
          subtotals: group.employees
            .sort((a, b) =>
              a.employee.fullName.localeCompare(b.employee.fullName),
            )
            .map((emp) => ({
              name: emp.employee.fullName,
              column1: emp.allocatedMembers,
              column2: emp.maxMembers,
            })),
        }));
      }

      return {
        id: pod.podId,
        name: pod.podName,
        totalAssigned: pod.allocation,
        totalCapacity: pod.maxMembers,
        allocations,
      };
    });
  }, [podForecasts, podStates]);

  if (!transformedData.length && !loading) {
    return (
      <PanelInfo
        type="info"
        title={translate('pods.shuffle.forecast.noData')}
      />
    );
  }

  const getUtilizationColor = (assigned: number, capacity: number): string => {
    const utilization = (assigned / capacity) * 100;
    if (utilization > 100) return Colors.error;
    return Colors.iconGreen;
  };

  return (
    <div className={styles.container}>
      {transformedData.map((forecast) => {
        const podState = podStates[forecast.id] || {
          isOpen: false,
          isLoading: false,
        };

        return (
          <Card key={forecast.id} className={styles.card}>
            <div
              className={styles.podHeader}
              onClick={() => handleTogglePod(forecast.id)}
            >
              <span className={styles.podTitle}>{forecast.name}</span>
              <div className={styles.totalInfo}>
                <PeopleIcon
                  htmlColor={getUtilizationColor(
                    forecast.totalAssigned,
                    forecast.totalCapacity,
                  )}
                  fontSize="large"
                />
                <span
                  className={styles.totalText}
                  style={{
                    color: getUtilizationColor(
                      forecast.totalAssigned,
                      forecast.totalCapacity,
                    ),
                  }}
                >
                  {forecast.totalAssigned}/{forecast.totalCapacity}
                </span>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTogglePod(forecast.id);
                  }}
                  size="small"
                >
                  {podState.isLoading ? (
                    <div className="flex center" style={{ width: 25 }}>
                      <Spinner width={20} />
                    </div>
                  ) : (
                    <CollapseIcon open={podState.isOpen} />
                  )}
                </IconButton>
              </div>
            </div>
            <Collapse open={podState.isOpen}>
              {forecast.allocations ? (
                <div className={styles.tableContainer}>
                  <RecursiveTable
                    data={forecast.allocations}
                    header="Employee"
                    header1="Assigned"
                    header2="Capacity"
                  />
                </div>
              ) : null}
            </Collapse>
          </Card>
        );
      })}
    </div>
  );
};
